/**
 * Step 3 — the application fee (invariant 4's one card-is-correct exception).
 *
 * Real Stripe Elements, not a placeholder: CLAUDE.md's step-10 note is
 * explicit that this "does not depend on the Stripe ACH blocker... can be
 * built while ACH is still stalled." `.env`'s Stripe keys are still
 * placeholder-shaped though (see `startApplicationFee` on the API side), so
 * this cannot be exercised end to end locally — `startApplicationFee`
 * throws a clean, readable error in that case (`APPLICATION_FEE_AMOUNT` /
 * Stripe keys unset) rather than this page crashing on a missing
 * clientSecret.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { api } from '../../lib/api.js';
import { FormErrors } from '../../components/Form.js';
import { ApplyProgress } from './ApplyProgress.js';

interface StartFeeResponse {
  clientSecret: string;
  publishableKey: string;
}

export function ApplicationFee() {
  const { id } = useParams<{ id: string }>();
  const [intent, setIntent] = useState<StartFeeResponse | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const result = await api<StartFeeResponse>(`/applications/${id}/fee/start`, {
          method: 'POST',
          body: JSON.stringify({}),
        });
        if (cancelled) return;
        setIntent(result);
        setStripePromise(loadStripe(result.publishableKey));
      } catch (caught) {
        if (!cancelled) setError(caught);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <section>
      <ApplyProgress step={3} />

      <div className="apply-hero">
        <p className="apply__eyebrow">Almost there</p>
        <h1>One last step.</h1>
        <p className="apply__lede">A one-time, non-refundable fee to process your application.</p>
      </div>

      {error ? (
        <div className="card">
          <FormErrors error={error} />
        </div>
      ) : !intent || !stripePromise || !id ? (
        <p className="muted">Preparing your payment…</p>
      ) : (
        <div className="card">
          <Elements stripe={stripePromise} options={{ clientSecret: intent.clientSecret }}>
            <FeeForm applicationId={id} />
          </Elements>
        </div>
      )}
    </section>
  );
}

function FeeForm({ applicationId }: { applicationId: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/apply/${applicationId}/submit`,
      },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message ?? 'The payment could not be completed.');
      setSubmitting(false);
      return;
    }

    void navigate(`/apply/${applicationId}/submit`);
  }

  return (
    <form className="stack" onSubmit={(event) => void onSubmit(event)}>
      <PaymentElement />
      {error ? <p className="alert alert--error">{error}</p> : null}
      <button className="button" type="submit" disabled={!stripe || submitting}>
        {submitting ? 'Processing…' : 'Pay and continue'}
      </button>
    </form>
  );
}
